import { useEffect, useMemo } from 'react';

import Store from 'helpers/store';
import Cache from 'helpers/cache';
import useStore from 'hooks/useStore';

import NotificationsService from 'services/notifications';
import { useFeatureToggleItems } from 'components/FeatureToggle';
import featureToggles from 'config/featureToggles';

const notificationsStore = new Store({
  active: [],
  past: [],
  isLoading: true,
}, 'notifications');

const useNotifications = (options = {}) => {
  const { autoFetch = false, refreshInterval = 0, fetch = 'active' } = options;

  const notificationTypeToggles = useFeatureToggleItems(featureToggles.NOTIFICATIONS);

  const actions = useMemo(() => {
    return createNotificationsActions(notificationsStore, notificationTypeToggles);
  }, [notificationTypeToggles]);

  const fetchAction = useMemo(() => {
    if (fetch === 'active') {
      return actions.getActive;
    } else if (fetch === 'past') {
      return actions.getPast;
    } else {
      throw new Error('Invalid fetch option', fetch);
    }
  }, [actions, fetch]);
  
  useEffect(() => {
    if (autoFetch) {
      fetchAction();
    }
  }, [fetchAction, autoFetch]);

  useEffect(() => {
    if (refreshInterval) {
      let interval = setInterval(() => {
        fetchAction();
      }, refreshInterval);

      return () => clearInterval(interval);
    }
  }, [fetchAction, refreshInterval]);

  const store = useStore(notificationsStore);

  return {
    state: store.get(),
    store,
    actions,
  };
};

const notificationsCache = new Cache();

const createNotificationsActions = (store, notificationTypeToggles) => {
  const service = new NotificationsService();

  const onNotificationsRead = ({ notification_id, notification_type }) => {
    const isRead = (n) => {
      if (notification_id === 0 || n.notification_id === notification_id) {
        if (!notification_type || n.notification_type === notification_type) {
          return true;
        }
      }
      return false;
    };

    store.set(state => ({
      active: state.active.map(n => {
        if (isRead(n)) {
          n.is_read = 1;
        }
        return n;
      }),
    }));
  };

  const filterByFeatureToggles = (notifications) => {
    return notifications.filter(notification => {
      const notificationTypeToggle = notificationTypeToggles.find(toggle => {
        return toggle.itemName === notification.notification_type;
      });

      if (notificationTypeToggle) {
        if (notificationTypeToggle.visible === false) {
          return false;
        }
      }

      return true;
    });
  };

  return {
    async getActive() {
      store.set(() => ({ isLoading: true }));
      return service.loadAll()
        .then(data => {
          if (!data) return;
          store.set(() => ({ active: filterByFeatureToggles(data) }));
        })
        .finally(() => {
          if (store.get().isLoading) {
            store.set(() => ({ isLoading: false }));
          }
        });
    },

    async getPast() {
      store.set(() => ({ isLoading: true }));
      return service.loadAll({ past_data: 1 })
        .then(data => {
          if (!data) return;
          store.set(() => ({ past: filterByFeatureToggles(data) }));
        })
        .finally(() => {
          if (store.get().isLoading) {
            store.set(() => ({ isLoading: false }));
          }
        });
    },

    async markAsRead({ notification_id }) {
      return service.markAsRead({ notification_id })
        .then(() => {
          onNotificationsRead({ notification_id });
        })
        .finally(() => {
          notificationsCache.clear();
        });
    },

    async markAsReadByType({ notification_type }) {
      return service.markAsRead({
        notification_id: 0,
        notification_type
      })
      .then(() => {
        onNotificationsRead({ notification_id: 0, notification_type });
      })
      .finally(() => {
        notificationsCache.clear();
      });
    }
  };
};

export default useNotifications;